import { brand, ink, rgba, semantic, surface } from '@repo/design-tokens';

/**
 * How each kind of region is drawn on the picture — ONE table (`multi-observer-modal`, FR8), every
 * colour a status role from Rob's status page (`semantic.status`; spec
 * `onboarding-status-colours-camera-picture`), so a hue means the same here as on every other step:
 *
 * - `unlinked`: work to do (periwinkle), dashed — the region still waits for its entity.
 * - `progress` and `selected`: being edited (butter), the look that stands out on CCTV — the region
 *   being drawn, and the one being edited, with larger handles.
 * - `hover`: the bold dashed frost line the design system draws hover with.
 * - `suggestion`: the engine's suggestion edge, dashed.
 * - `machineOutline`: information (lavender), dotted and unfilled.
 * - `ghost`: every other region, a faint frost outline. Context, not a status.
 *
 * Fills are the role's own mark at the plotter's light alphas rather than the role's tag wash: the
 * wash (0.24 to 0.42) is too heavy over a camera frame. The Edit dim and the keyboard corner below
 * are not statuses either, so they keep their own colours. `regionStyle.test.ts` holds the table to
 * the roles.
 */
export type RegionKind =
  | 'progress'
  | 'unlinked'
  | 'selected'
  | 'hover'
  | 'ghost'
  | 'suggestion'
  | 'machineOutline'
  | 'heatOutline'
  | 'heatOutlineHighlight';

export interface RegionStyle {
  stroke: string;
  fill: string;
  width: number;
  /** SVG `stroke-dasharray`, or `undefined` for a solid line. */
  dash?: string;
  /** SVG `stroke-linecap`; `round` turns short dashes into dots. Default the SVG default. */
  lineCap?: 'butt' | 'round';
  /** Corner handle radius; `0` draws none. */
  handleR: number;
  handleFill: string;
  handleStroke: string;
  handleWidth: number;
}

const WHITE = surface[0];

const { roles, hover, suggestion } = semantic.status;

export const REGION_STYLE: Record<RegionKind, RegionStyle> = {
  progress: {
    stroke: roles.beingEdited.mark,
    fill: 'none',
    width: 3,
    handleR: 6,
    handleFill: WHITE,
    handleStroke: roles.beingEdited.mark,
    handleWidth: 3,
  },
  unlinked: {
    stroke: roles.workToDo.mark,
    fill: rgba(roles.workToDo.mark, 0.18),
    width: 3,
    dash: '10 7',
    handleR: 6,
    handleFill: WHITE,
    handleStroke: roles.workToDo.mark,
    handleWidth: 3,
  },
  selected: {
    stroke: roles.beingEdited.mark,
    fill: rgba(roles.beingEdited.mark, 0.24),
    width: 3.5,
    handleR: 11,
    handleFill: WHITE,
    handleStroke: roles.beingEdited.mark,
    handleWidth: 4,
  },
  hover: {
    stroke: hover.edge,
    fill: rgba(hover.edge, 0.16),
    width: 3,
    dash: '10 7',
    handleR: 0,
    handleFill: 'none',
    handleStroke: 'none',
    handleWidth: 0,
  },
  ghost: {
    stroke: rgba(brand.frost, 0.6),
    fill: 'none',
    width: 1.5,
    dash: '5 5',
    handleR: 0,
    handleFill: 'none',
    handleStroke: 'none',
    handleWidth: 0,
  },
  /*
   * An observer the suggestion engine proposes (`observer-autogen-review-ui-preview`, R4): the status
   * page's suggestion edge, dashed, never interactive. Read-only, so no handles.
   */
  suggestion: {
    stroke: suggestion.edge,
    fill: rgba(suggestion.edge, 0.14),
    width: 2.5,
    dash: '8 6',
    handleR: 0,
    handleFill: 'none',
    handleStroke: 'none',
    handleWidth: 0,
  },
  /*
   * Where the suggestion engine thinks a machine is (`observer-matching-beyond-geometry-machine-outline`):
   * its floor-plan outline through the camera's calibration. Information (lavender): a fact worth
   * seeing that asks nothing of you. Dotted (round caps on short dashes) and unfilled, so it never
   * reads as a suggestion or a drawn observer without relying on colour. Read-only, so no handles.
   */
  machineOutline: {
    stroke: roles.information.mark,
    fill: 'none',
    width: 2,
    dash: '2 5',
    lineCap: 'round',
    handleR: 0,
    handleFill: 'none',
    handleStroke: 'none',
    handleWidth: 0,
  },
  /*
   * A blob of heat a person can take as a region (`observer-matching-beyond-geometry-heat-outlines`):
   * the suggestion edge, since it is an offer, but solid and unfilled so it never reads as one of the
   * engine's dashed suggestions. Read-only, so no handles.
   */
  heatOutline: {
    stroke: suggestion.edge,
    fill: 'none',
    width: 2,
    handleR: 0,
    handleFill: 'none',
    handleStroke: 'none',
    handleWidth: 0,
  },
  /** The heat outline the keys have reached: the selection colour, washed, so Enter's target is plain. */
  heatOutlineHighlight: {
    stroke: roles.beingEdited.mark,
    fill: rgba(roles.beingEdited.mark, 0.18),
    width: 3,
    handleR: 0,
    handleFill: 'none',
    handleStroke: 'none',
    handleWidth: 0,
  },
};

/** The keyboard-selected corner: the focus ring's own line colour, so it reads as focus. */
export const KEYBOARD_HANDLE_FILL = brand.cyanPress;

/** The wash over the rest of the picture while a region is being edited. */
export const EDIT_DIM = rgba(ink[1], 0.45);

/**
 * Which kind one of the plotter's OWN regions is, from what the host told it. There is one look,
 * the editing one (`observer-unified-draw-edit`, R1.3): the region being drawn; a region waiting for
 * its entity; the region being edited (`active`), drawn as being edited; and the entity's other
 * regions on the camera, the faint frost outline until one of them is picked (review: every one of
 * them was lit and undimmed).
 */
export const ownRegionKind = ({
  isCurrent,
  waiting,
  active = true,
}: {
  isCurrent: boolean;
  waiting: boolean;
  active?: boolean;
}): RegionKind => (isCurrent ? 'progress' : waiting ? 'unlinked' : active ? 'selected' : 'ghost');
